import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import { Header } from '../components/Header';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);

  // Define sections for the privacy policy
  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: `
        <p>At My Football Game, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").</p>
        <p>Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the Service.</p>
      `
    },
    {
      id: 'collection',
      title: 'Information We Collect',
      content: `
        <p>We collect several types of information for various purposes to provide and improve our Service to you:</p>
        <h4 class="font-semibold my-2 text-blue-300">Personal Data</h4>
        <p>While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). This may include, but is not limited to:</p>
        <ul class="list-disc pl-5 space-y-1 my-2">
          <li>Email address</li>
          <li>First name and last name</li>
          <li>Phone number</li>
          <li>Address, State, Province, ZIP/Postal code, City</li>
          <li>Cookies and Usage Data</li>
        </ul>
        
        <h4 class="font-semibold my-2 text-blue-300">Usage Data</h4>
        <p>We may also collect information that your browser sends whenever you visit our Service or when you access the Service by or through a mobile device ("Usage Data").</p>
        <p>This Usage Data may include information such as your computer's Internet Protocol address (e.g., IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers, and other diagnostic data.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Game Data</h4>
        <p>When you use the My Football Game service, we collect data about your in-game activity, including:</p>
        <ul class="list-disc pl-5 space-y-1 my-2">
          <li>Game progress and achievements</li>
          <li>In-game purchases and virtual goods</li>
          <li>Game preferences and settings</li>
          <li>Interactions with other players</li>
          <li>Play patterns and statistics</li>
        </ul>
      `
    },
    {
      id: 'use',
      title: 'How We Use Your Information',
      content: `
        <p>We use the collected data for various purposes:</p>
        <ul class="list-disc pl-5 space-y-1 my-2">
          <li>To provide and maintain our Service</li>
          <li>To notify you about changes to our Service</li>
          <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
          <li>To provide customer support</li>
          <li>To gather analysis or valuable information so that we can improve our Service</li>
          <li>To monitor the usage of our Service</li>
          <li>To detect, prevent and address technical issues</li>
          <li>To fulfill any other purpose for which you provide it</li>
          <li>To personalize your gaming experience</li>
          <li>To process transactions and send related information including confirmations and invoices</li>
          <li>To send you technical notices, updates, security alerts, and support and administrative messages</li>
        </ul>
      `
    },
    {
      id: 'sharing',
      title: 'Information Sharing and Disclosure',
      content: `
        <p>We may share your personal information in the following situations:</p>
        
        <h4 class="font-semibold my-2 text-blue-300">With Service Providers</h4>
        <p>We may share your personal information with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf and require access to such information to do that work.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">For Business Transfers</h4>
        <p>We may share or transfer your personal information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">With Affiliates</h4>
        <p>We may share your information with our affiliates, in which case we will require those affiliates to honor this Privacy Policy.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">With Business Partners</h4>
        <p>We may share your information with our business partners to offer you certain products, services, or promotions.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">With Other Users</h4>
        <p>When you share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Legal Requirements</h4>
        <p>We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process, such as in response to a court order or a subpoena.</p>
      `
    },
    {
      id: 'security',
      title: 'Data Security',
      content: `
        <p>The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.</p>
        <p>We implement a variety of security measures to maintain the safety of your personal information when you place an order or enter, submit, or access your personal information, including:</p>
        <ul class="list-disc pl-5 space-y-1 my-2">
          <li>All sensitive information you provide is encrypted via Secure Socket Layer (SSL) technology</li>
          <li>We implement a variety of security measures when a user enters, submits, or accesses their information</li>
          <li>All transactions are processed through a secure gateway provider and are not stored or processed on our servers</li>
        </ul>
      `
    },
    {
      id: 'analytics',
      title: 'Analytics and Advertising',
      content: `
        <p>We may use third-party Service Providers to monitor and analyze the use of our Service and to advertise our Service to you.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Google Analytics</h4>
        <p>Google Analytics is a web analytics service offered by Google that tracks and reports website traffic. Google uses the data collected to track and monitor the use of our Service. This data is shared with other Google services. Google may use the collected data to contextualize and personalize the ads of its own advertising network.</p>
        <p>You can opt-out of having made your activity on the Service available to Google Analytics by installing the Google Analytics opt-out browser add-on. The add-on prevents the Google Analytics JavaScript (ga.js, analytics.js, and dc.js) from sharing information with Google Analytics about visits activity.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Advertising</h4>
        <p>We may use third-party Service Providers to show advertisements to you to help support and maintain our Service.</p>
        <p>We, along with third-party vendors such as Google, use first-party cookies and third-party cookies or other third-party identifiers to compile data regarding user interactions with ad impressions and other ad service functions as they relate to our Service.</p>
        <p>Users can set preferences for how Google advertises to you using the Google Ad Settings page. Alternatively, you can opt out by visiting the Network Advertising Initiative Opt Out page or by using the Google Analytics Opt Out Browser add on.</p>
      `
    },
    {
      id: 'children',
      title: 'Children\'s Privacy',
      content: `
        <p>Our Service is not directed to anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us.</p>
        <p>If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.</p>
      `
    },
    {
      id: 'international',
      title: 'International Data Transfers',
      content: `
        <p>Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.</p>
        <p>If you are located outside the United States and choose to provide information to us, please note that we transfer the data, including Personal Data, to the United States and process it there.</p>
        <p>Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.</p>
      `
    },
    {
      id: 'rights',
      title: 'Your Data Protection Rights',
      content: `
        <p>Depending on your location and applicable laws, you may have certain rights regarding your personal information, including:</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Access</h4>
        <p>You may request copies of your personal information that we hold.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Rectification</h4>
        <p>You may request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Erasure</h4>
        <p>You may request that we erase your personal information under certain conditions.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Restrict Processing</h4>
        <p>You may request that we restrict the processing of your personal information under certain conditions.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Object to Processing</h4>
        <p>You may object to our processing of your personal information under certain conditions.</p>
        
        <h4 class="font-semibold my-2 text-blue-300">Data Portability</h4>
        <p>You may request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</p>
        
        <p>If you would like to exercise any of these rights, please contact us at the contact information provided below.</p>
      `
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      content: `
        <p>We use cookies and similar tracking technologies to track activity on our Service and hold certain information.</p>
        <p>Cookies are files with a small amount of data which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device. Tracking technologies also used are beacons, tags, and scripts to collect and track information and to improve and analyze our Service.</p>
        <p>You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.</p>
        <p>Examples of Cookies we use:</p>
        <ul class="list-disc pl-5 space-y-1 my-2">
          <li><strong>Session Cookies:</strong> We use Session Cookies to operate our Service.</li>
          <li><strong>Preference Cookies:</strong> We use Preference Cookies to remember your preferences and various settings.</li>
          <li><strong>Security Cookies:</strong> We use Security Cookies for security purposes.</li>
          <li><strong>Advertising Cookies:</strong> Advertising Cookies are used to serve you with advertisements that may be relevant to you and your interests.</li>
        </ul>
      `
    },
    {
      id: 'changes',
      title: 'Changes to This Privacy Policy',
      content: `
        <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy.</p>
        <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
      `
    },
    {
      id: 'contact',
      title: 'Contact Us',
      content: `
        <p>If you have any questions about this Privacy Policy, please contact us:</p>
        <p>If you have any questions about these Terms, please contact us:</p>
        Email: andreas.thomae@kampanos.pt
      `
    }
  ];

  const handleExpandToggle = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  return (
    <motion.div 
      className="w-full min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <Header />

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 shadow-xl mb-8"
        >
          <p className="text-white/90">
            At My Football Game, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our game and services.
          </p>
        </motion.div>

        {/* Table of contents */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mb-8 bg-gradient-to-r from-blue-900/20 to-indigo-900/20 rounded-xl p-5 border border-blue-900/20"
        >
          <h2 className="text-xl font-bold mb-3">Table of Contents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section, index) => (
              <motion.button
                key={section.id}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
                className="text-left flex items-center text-white/80 hover:text-white/100 transition-colors"
                onClick={() => handleExpandToggle(section.id)}
              >
                <span className="w-6 h-6 rounded-full bg-blue-900/30 flex items-center justify-center mr-2 text-xs">
                  {index + 1}
                </span>
                {section.title}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Accordion sections */}
        <div className="space-y-4">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + (index * 0.05) }}
              className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden shadow-md"
              id={section.id}
            >
              <button
                onClick={() => handleExpandToggle(section.id)}
                className="w-full px-5 py-4 text-left flex justify-between items-center hover:bg-white/[0.05] transition-colors"
              >
                <h2 className="text-lg font-semibold">{section.title}</h2>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`w-5 h-5 transition-transform ${expanded === section.id ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {expanded === section.id && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-5 py-4 border-t border-white/10 text-white/80"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              )}
            </motion.div>
          ))}
        </div>

        {/* Agreement section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mt-10 bg-gradient-to-r from-blue-900/30 to-indigo-900/30 p-6 rounded-xl border border-blue-500/20"
        >
          <h3 className="text-xl font-bold mb-3 text-center">Your Privacy Matters</h3>
          <p className="text-white/80 text-center mb-6">
            By using My Football Game, you agree to the collection and use of information in accordance with this Privacy Policy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/terms-of-service')} 
              className="px-6 py-2.5 bg-[var(--accent)] hover:bg-[var(--accent)] rounded-lg font-medium transition-colors shadow-md"
            >
              View Terms of Service
            </button>
            <button
              onClick={() => navigate('/')} 
              className="px-6 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
            >
              Return to Home
            </button>
          </div>
        </motion.div>
      </div>

      <Footer />
    </motion.div>
  );
};

export default PrivacyPolicy;